import {
  type ExpressionPath,
  type Type,
  matchExpression,
  getMeta,
  getSystem,
  withMeta,
  withSystem,
  Types,
  Expressions,
  Component,
  Plugins,
} from '@packages/systems/wfdl';
import type {RelativePositionPlacementOptions} from './types';
import {
  emptyArray,
  thrush,
  constantTrue,
  nth,
  concatTo,
  concat,
  constantFalse,
} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {type Option, None, fromNullable} from '@packages/utilities/fp/option';

import {
  TRecord,
  EElement,
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  isUserComponentInst,
  isUserComponent,
  isComponentInst,
} from './ComponentElementUtils';
// Direct-file import to avoid a cyclic import when core-elements is loaded
// during platform's index evaluation: platform → core-elements →
// user-components/utils barrel → getCount → platform.
// eslint-disable-next-line webflow/module-top-level-imports
import {getDataTypeFromElement} from '@packages/systems/user-components/utils/getDataTypeFromElement';
import {
  MutableQualifiedMap,
  Qualified,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {
  getCurrentOrDefaultExpression,
  getFieldDefaultExpression,
} from '@packages/systems/core/component-conversions';
import {GuidGenerator} from '@packages/utilities/guid';
import {pair} from '@packages/utilities/Pair';
import uuidv5 from 'uuid/v5';
import {addBackHiddenProperties} from './hiddenDataHack';

export const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

/**
 * Returns a list of children elements if children exists, otherwise
 * returns undefined.
 */
export function getChildren(
  element: ExpressionElement
): undefined | Array<ExpressionElement> {
  const data = Expressions.getElementData(element);
  const children = Expressions.getIn(data, CHILDREN_PATH);
  const childrenList = children ? Expressions.getList(children) : null;

  if (!childrenList) {
    return undefined;
  }

  return childrenList.reduce<Array<ExpressionElement>>((result, e) => {
    const el = Expressions.getElement(e);
    if (el) {
      result.push(el);
    }
    return result;
  }, []);
}

export function reduceElementTree<V>(
  element: ExpressionElement,
  reducer: (arg1: V, arg2: ExpressionElement) => V,
  initialValue: V
): V {
  const children = getChildren(element) || emptyArray;

  return children.reduce(
    (value, childElement) => reduceElementTree(childElement, reducer, value),
    reducer(initialValue, element)
  );
}

export function findChild(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): undefined | ExpressionElement {
  const children = getChildren(element);
  return children && children.find(predicate);
}

export function findAllChildren(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): Array<ExpressionElement> {
  const children = getChildren(element);
  return children ? children.filter(predicate) : [];
}

export function updateElementChildren(
  element: ExpressionElement,
  update: (children: Expression) => Expression
): ExpressionElement {
  const dataExpr = Expressions.getElementData(element);
  const children = Expressions.getIn(dataExpr, CHILDREN_PATH);
  if (!children) return element;
  const nextChildren = update(children);
  if (nextChildren === children) return element;
  const nextDataExpr = Expressions.setIn(dataExpr, CHILDREN_PATH, nextChildren);
  return Expressions.setElementData(element, nextDataExpr);
}

const applyPredicateToElement = matchExpression<
  (arg1: (arg: ExpressionElement) => boolean) => boolean
>({
  Element: thrush,
  _: (_) => constantTrue,
});
export function filterElementChildren(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement {
  return updateElementChildren(element, (children) => {
    const childrenList = Expressions.getList(children);
    /*
    We can't know the type of `children` expression in element data beforehand.
    This implementation works for elements where the `children` in element data
    is a List expression.

    If `children` is not present, not a List expression, or has an empty value,
    we do nothing and return the input element.
    */
    if (!childrenList || childrenList.length === 0) {
      return children;
    }

    /*
    We can't know the types of expressions stored in `children` either.
    This implementation applies the predicate to element entries in `children`
    and ignores any non-element entries.
    */
    const expressionPredicate = (childExpr: Expression) => {
      return applyPredicateToElement(childExpr)(predicate);
    };

    const nextChildrenVal = childrenList.filter(expressionPredicate);

    if (nextChildrenVal.length === childrenList.length) {
      return children;
    }
    return EList(nextChildrenVal);
  });
}

const getElementAndStop = matchExpression({
  // if expression is an element, we return it and stop recursing
  Element: (element) => Expressions.stop([element]),
  // otherwise we continue looking for descendants
  _: (_) => Expressions.resume(emptyArray),
});

const concatElements =
  (a: ExpressionElement[]) =>
  (b: ExpressionElement[]): ExpressionElement[] => {
    if (a === emptyArray) {
      return b;
    }

    if (b === emptyArray) {
      return a;
    }

    return [...a, ...b];
  };

/**
 * Returns direct Element descendants found in `expr`
 *
 * Does a depth-first search through `expr` for Element expressions.
 * Stops recursing deeper after each encountered Element, i.e. this
 * function does not return children of children.
 */
export const getDescendantsFromExpression: (
  expr: Expression
) => ExpressionElement[] =
  Expressions.everything(concatElements)(getElementAndStop);

/**
 * Returns direct Element descendants of `element`
 *
 * Does a depth-first search through `element` data for Element expressions.
 * Stops recursing after each encountered Element descendants, i.e. this
 * function does not return children of children.
 */
export function getDescendants(
  element: ExpressionElement
): ExpressionElement[] {
  return getDescendantsFromExpression(Expressions.getElementData(element));
}

/**
 * Equivalent to TreeUtils::findChildren, but for expression element.
 *
 * Returns the first descendant child element that matches the predicate,
 * otherwise returns undefined.
 */
export function findElementDescendant(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement | undefined {
  let result;
  const children = getChildren(element) || [];

  if (
    children.some((childElement) => {
      if (predicate(childElement)) {
        result = childElement;

        return true;
      } else {
        result = findElementDescendant(childElement, predicate);

        return result !== undefined;
      }
    })
  ) {
    return result;
  } else {
    return undefined;
  }
}

/**
 * Similar to `filterElementChildren` above, except it filters entire tree.
 */
export function filterElementTree(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement {
  const dataExpr = Expressions.getElementData(element);
  const children = Expressions.getIn(dataExpr, CHILDREN_PATH);
  const childrenList = children ? Expressions.getList(children) : null;
  /*
    We can't know the type of `children` expression in element data beforehand.
    This implementation works for elements where the `children` in element data
    is a List expression.

    If `children` is not present, not a List expression, or has an empty value,
    we do nothing and return the input element.
  */
  if (!childrenList || childrenList.length === 0) {
    return element;
  }

  const filteredChildrenVal = childrenList.reduce<Array<Expression>>(
    (result, childExpr) => {
      const childElement = Expressions.getElement(childExpr);
      if (!childElement) {
        result.push(childExpr);
        return result;
      }

      if (predicate(childElement)) {
        const nextChildElement = filterElementTree(childElement, predicate);

        result.push(
          childElement === nextChildElement
            ? childExpr
            : EElement(nextChildElement)
        );
      }

      return result;
    },
    []
  );

  const changed =
    filteredChildrenVal.length !== childrenList.length ||
    filteredChildrenVal.some((childExpr, i) => childExpr !== childrenList[i]);

  if (!changed) {
    return element;
  }

  const nextDataExpr = Expressions.setIn(
    dataExpr,
    CHILDREN_PATH,
    EList(filteredChildrenVal)
  );

  return Expressions.setElementData(element, nextDataExpr);
}

/**
  Get Id from Expression Element
 */
export const getExprElementId = matchExpression({
  Element: Expressions.getElementId,
  _: (_) => undefined,
});

export const updateElementListAtPath = (
  root: Expression,
  path: ExpressionPath,
  updater: (arg1: Array<Expression>, arg2: number) => Expression
): Expression => {
  const anchorExprPosition = path[path.length - 1];
  if (!anchorExprPosition || anchorExprPosition.in !== 'List') {
    return root;
  }
  const {index} = anchorExprPosition;

  const pathToParentChildren = path.slice(0, -1);

  return Expressions.updateIn(root, pathToParentChildren, (listExpr) => {
    const list = Expressions.getList(listExpr);

    return list ? updater(list, index) : listExpr;
  });
};

const addElementAtPosition = {
  before: (
    root: Expression,
    path: ExpressionPath,
    newElement: ExpressionElement
  ): Expression =>
    updateElementListAtPath(root, path, (list, index) =>
      EList([
        ...list.slice(0, index),
        EElement(newElement),
        ...list.slice(index),
      ])
    ),

  after: (
    root: Expression,
    path: ExpressionPath,
    newElement: ExpressionElement
  ): Expression =>
    updateElementListAtPath(root, path, (list, index) =>
      EList([
        ...list.slice(0, index + 1),
        EElement(newElement),
        ...list.slice(index + 1),
      ])
    ),

  prepend: (
    root: Expression,
    path: ExpressionPath,
    newElement: ExpressionElement
  ): Expression =>
    Expressions.updateIn(
      root,
      [...path, {in: 'Element'}, ...CHILDREN_PATH],
      (listExpr) => {
        const list = Expressions.getList(listExpr);
        return list ? EList([EElement(newElement), ...list]) : listExpr;
      }
    ),

  append: (
    root: Expression,
    path: ExpressionPath,
    newElement: ExpressionElement
  ): Expression =>
    Expressions.updateIn(
      root,
      [...path, {in: 'Element'}, ...CHILDREN_PATH],
      (listExpr) => {
        const list = Expressions.getList(listExpr);
        return list ? EList([...list, EElement(newElement)]) : listExpr;
      }
    ),
} as const;

// Adds newElement in the root expression in the anchor position determined by anchor element
export const addElementAtAnchorInPath = (
  root: Expression,
  newElement: ExpressionElement,
  anchorElement: ExpressionElement,
  anchorPosition: RelativePositionPlacementOptions,
  anchorPath: ExpressionPath | null,
  componentMap: Component.ComponentMap
): Expression => {
  const path = Expressions.findPath(
    root,
    (expr) => getExprElementId(expr) === anchorElement.id
  );
  if (!path) return root;

  if (
    anchorPosition === 'before' ||
    anchorPosition === 'after' ||
    !isUserComponentInst(componentMap, anchorElement)
  ) {
    return addElementAtAnchor(root, newElement, anchorElement, anchorPosition);
  }

  if (anchorPath === null) {
    return root;
  }

  return Expressions.updateElement(
    anchorElement.id,
    (element) => {
      const data = Expressions.getElementData(element);
      const listExpr = Expressions.getIn(data, anchorPath);

      let updatedList;

      if (listExpr) {
        const list = Expressions.getList(listExpr);
        if (!list) {
          return element;
        }
        updatedList =
          anchorPosition === 'append'
            ? [...list, EElement(newElement)]
            : [EElement(newElement), ...list];
      } else {
        updatedList = [EElement(newElement)];
      }

      const updatedData = Expressions.sparseSetIn(
        data,
        anchorPath,
        EList(updatedList)
      );

      return Expressions.setElementData(element, updatedData);
    },
    root
  );
};

// Adds newElement in the root expression in the anchor position determined by anchor element
export const addElementAtAnchor = (
  root: Expression,
  newElement: ExpressionElement,
  anchorElement: ExpressionElement,
  anchorPosition: RelativePositionPlacementOptions
): Expression => {
  const path = Expressions.findPath(
    root,
    (expr) => getExprElementId(expr) === anchorElement.id
  );
  if (!path) return root;
  return addElementAtPosition[anchorPosition](root, path, newElement);
};

export const addElementAtAnchorById = (
  root: Expression,
  newElement: ExpressionElement,
  anchorElementId: string,
  anchorPosition: RelativePositionPlacementOptions
): Expression => {
  const path = Expressions.findPath(
    root,
    (expr) => getExprElementId(expr) === anchorElementId
  );

  if (!path) return root;
  return addElementAtPosition[anchorPosition](root, path, newElement);
};

// Removes element from the root expression
export const removeElementById = (
  root: Expression,
  elementId: string
): Expression => {
  const path = Expressions.findPath(
    root,
    (expr) => getExprElementId(expr) === elementId
  );

  if (!path) return root;

  return updateElementListAtPath(root, path, (list, index) =>
    EList(list.filter((_, i) => i !== index))
  );
};

const memoizedElementId = cacheMemo(10000);

/**
 * Returns an Array of Elements from the provided rootElement down to the
 * provided innerElement.
 * i.g: [Body (rootElement), Container, Div, Heading (innerElement)]
 */
export const getElementWithAncestors: (
  rootElement: Expression
) => (id: string) => Array<ExpressionElement> = weakMemo(
  (rootElement: Expression) =>
    memoizedElementId((id: string): Array<ExpressionElement> => {
      const result: Array<ExpressionElement> = [];

      const go = (element: ExpressionElement): ExpressionElement[] => {
        result.push(element);
        const parent = Expressions.getParent(rootElement, element.id);
        if (!parent) return result.reverse();
        return go(parent);
      };

      const initialEl = Expressions.getElementById(rootElement, id);
      if (!initialEl) return result;
      return go(initialEl);
    })
);

/**
 * Returns a tuple containing [ancestors, element] where:
 * - `ancestors` is an Array of `ExpressionElements`, beginning with the outer-most parent contained in `rootExpression`,
 *   down to the nearest parent of the inner initial element.
 * - `element` is the initial, inner-most element found in `rootExpression` via `Expressions.getElementById`.
 *
 * @example
 * [[Body (root element), Container, Div], Heading (inner element)]
 */
export const getSplitElementWithAncestors: (
  rootExpression: Expression
) => (id: string) => [ExpressionElement[], ExpressionElement | null] = weakMemo(
  (rootExpression: Expression) =>
    memoizedElementId(
      (id: string): [ExpressionElement[], ExpressionElement | null] => {
        const initialEl = Expressions.getElementById(rootExpression, id);
        if (!initialEl) return [[], null];
        const ancestors = [];
        let el: null | ExpressionElement = initialEl;
        while (el) {
          const parent = Expressions.getParent(rootExpression, el.id);
          if (parent) {
            ancestors.unshift(parent);
          }
          el = parent;
        }

        return [ancestors, initialEl];
      }
    )
);

/**
 * Finds the furthest element that matches the predicate
 */
export function findFurthestAncestor(
  root: Expression,
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement | undefined {
  return getElementWithAncestors(root)(element.id).find(predicate);
}

/**
 * Gets an array of all the ancestor IDs.
 */
export function getAncestorIds(
  root: Expression,
  elementId: string
): Array<string> {
  const parent = Expressions.getParent(root, elementId);

  return parent ? getAncestorIds(root, parent.id).concat(parent.id) : [];
}

/**
 * Gets an array of IDs from the root element to the provided `elementId`.
 */
export function getIdsToElement(rootExpr: Expression, elementId: string) {
  return getAncestorIds(rootExpr, elementId).concat(elementId);
}

/**
 * Find the first element within the provided element for which the given
 * predicate returns true.
 *
 * ```jsx
 * const firstTextChild = findElement(myElement, isText);
 * ```
 */
export function findElement(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement | null {
  const elems = Expressions.allElements(EElement(element));
  const ids = Object.keys(elems);
  for (const id of ids) {
    const el = elems[id];
    // @ts-expect-error - TS2345 - Argument of type 'ExpressionElement | undefined' is not assignable to parameter of type 'ExpressionElement'. | TS2322 - Type 'ExpressionElement | undefined' is not assignable to type 'ExpressionElement | null'.
    if (predicate(el)) return el;
  }
  return null;
}

/**
 * Finds and returns first element in tree that matches predicate.
 *
 * Recurses into user component instances, avoiding infinite loops and
 * searching the same component instances twice.
 */
export function findElementInComponentTree(
  tree: ExpressionElement,
  predicate: (element: ExpressionElement) => boolean,
  componentMap: Component.ComponentMap,
  mut_visitTracker: MutableQualifiedMap<
    string,
    true
  > = new MutableQualifiedMap()
): ExpressionElement | null {
  const elems = Expressions.allElements(EElement(tree));
  const ids = Object.keys(elems);
  for (const id of ids) {
    const el = elems[id];
    if (!el) {
      continue;
    }

    if (predicate(el)) {
      return el;
    }

    if (
      isUserComponentInst(componentMap, el) &&
      !mut_visitTracker.get(el.type)
    ) {
      mut_visitTracker.set_mut(el.type, true);
      const component = componentMap.get(el.type);
      const componentRootEl = component
        ? Expressions.getElement(Component.getRender(component))
        : null;
      if (componentRootEl) {
        const result = findElementInComponentTree(
          componentRootEl,
          predicate,
          componentMap,
          mut_visitTracker
        );
        if (result) {
          return result;
        }
      }
    }
  }
  return null;
}

/**
 * Returns true if the given element contains an element that matches the predicate.
 *
 * ```jsx
 * const hasText = contains(myElement, isText);
 * ```
 */
export function contains(
  element: ExpressionElement,
  predicate: (arg1: ExpressionElement) => boolean
): boolean {
  return findElement(element, predicate) !== null;
}
const memoizeElementId = cacheMemo(100);

const __findIndexById = memoizeElementId((id: string) =>
  weakMemo((elements: Array<ExpressionElement>) => {
    return elements.findIndex((element) => {
      return element.id === id;
    });
  })
);

export const findIndexById = (
  id: string,
  elements: Array<ExpressionElement>
): number => __findIndexById(id)(elements);

const seek =
  (delta: number) =>
  (id: string) =>
  (elements: Array<ExpressionElement>): Option<ExpressionElement> => {
    const index = findIndexById(id, elements);
    return index === -1 ? None : nth(index + delta)(elements);
  };

export const maybeGetParent =
  (id: string): ((root: Expression) => Option<ExpressionElement>) =>
  (root: Expression): Option<ExpressionElement> =>
    fromNullable(Expressions.getParent(root, id));

const seekSibling = (
  delta: number
): ((id: string) => (root: Expression) => Option<ExpressionElement>) => {
  const seekDelta = seek(delta);
  return (id: string) => (root: Expression) =>
    maybeGetParent(id)(root)
      .map((el) => getChildren(el) || [])
      .chain(seekDelta(id));
};

/**
 * Returns the prev sibling for an element.
 * @param  {Record}  root   The root tree element.
 * @param  {String}  id     The ID of the element for which to find the prev sibling.
 * @return {Option<Record>} The prev sibling.
 */
export const getPrev: (
  id: string
) => (root: Expression) => Option<ExpressionElement> = seekSibling(-1);

/**
 * Returns the next sibling for an element.
 * @param  {Record}  tree   The root tree element.
 * @param  {String}  id     The ID of the element for which to find the next sibling.
 * @return {Option<Record>} The next sibling.
 */
export const getNext: (
  id: string
) => (root: Expression) => Option<ExpressionElement> = seekSibling(1);

/**
 * Swaps out the currentElement in a root with the newElement
   passed in, then returns the updated root
 */
export const replaceElement = (
  root: Expression,
  currentElement: ExpressionElement,
  newElement: Expression
): Expression => {
  return replaceElementById(root, currentElement.id, newElement);
};

/**
 * Swaps out the currentElement in a root with the newElement
   passed in, then returns the updated root
 */
export const replaceElementById = (
  root: Expression,
  id: string,
  newElement: Expression
): Expression => {
  const path = Expressions.findPath(root, (expr) => {
    return getExprElementId(expr) === id;
  });

  if (!path) return root;

  return Expressions.setIn(root, path, newElement);
};

/**
 * Gets the keypath to an element from the tree root.
 */
export const getKeyPathToElement = (
  root: Expression,
  elementId: string
): ExpressionPath | null => {
  return Expressions.findPath(
    root,
    (expr) => getExprElementId(expr) === elementId
  );
};

export const flatten: (element: ExpressionElement) => Array<ExpressionElement> =
  weakMemo((element: ExpressionElement): Array<ExpressionElement> => {
    const children = getChildren(element);
    if (children) {
      return [element].concat(children.flatMap(flatten));
    }
    return [element];
  });

/**
 * Removes a Type field at ExpressionPath from dataType
 */
export const removeDataTypeField = (
  dataType: Type,
  expressionPath: ExpressionPath
): Type => {
  const fieldContainerPath = expressionPath.slice(0, -1);
  const fieldContainerDataType = Types.__DEPRECATED__getTypeAtExpressionPath(
    dataType,
    fieldContainerPath
  );
  if (!fieldContainerDataType) {
    throw new Error('Missing Record Type');
  }
  const fieldContainerRecordFields = Types.getRecordFields(
    fieldContainerDataType
  );

  if (!fieldContainerRecordFields) {
    throw new TypeError(
      `Expected a Record Type, but got ${Types.print(fieldContainerDataType)}`
    );
  }
  const leafPath = expressionPath.slice(-1)[0];
  // @ts-expect-error - TS18048 - 'leafPath' is possibly 'undefined'.
  if (leafPath.in !== 'Record') {
    throw new TypeError(
      `Expected Record path at leaf, but got ${JSON.stringify(leafPath)}`
    );
  }
  let nextFieldContainerDataType = TRecord(
    dissoc(leafPath.at)(fieldContainerRecordFields)
  );
  const meta = getMeta(fieldContainerDataType);
  const system = getSystem(fieldContainerDataType);

  if (meta)
    nextFieldContainerDataType = withMeta(meta, nextFieldContainerDataType);
  if (system)
    nextFieldContainerDataType = withSystem(system, nextFieldContainerDataType);
  return Types.setTypeAtBindingPath(
    dataType,
    fieldContainerPath,
    nextFieldContainerDataType
  );
};

const createMatcherWithPredicate = weakMemo(
  (predicate: (arg1: ExpressionElement) => boolean) => {
    return matchExpression({
      Element: (el) =>
        predicate(el)
          ? Expressions.resume([el])
          : Expressions.resume(emptyArray),
      _: () => Expressions.resume(emptyArray),
    });
  }
);

/**
 * Searches a given expression for all elements that match the predicate
 * and returns a flattened array with all matching elements.
 *
 * This function will find children of children.
 *
 * e.g. for a given expression with a structure like this:
 * - Div Block 1
 * - Paragraph 1
 * - Div Block 2
 * -- Div Block 3
 * --- Div Block 4
 *
 * and with a predicate that returns true when the element is a div block,
 *
 * the result will be an array containing [Div Block 1, Div Block 2, Div Block 3, Div Block 4]
 */
export function findAllDescendantsFromExpression(
  expr: Expression,
  predicate: (arg1: ExpressionElement) => boolean
): ExpressionElement[] {
  return Expressions.everything<ExpressionElement[]>(concatTo)(
    createMatcherWithPredicate(predicate)
  )(expr);
}

const resumeEmpty = Expressions.resume(emptyArray);

const getUserComponentType = matchExpression({
  Element: ({type}) => {
    return isUserComponent(type) ? Expressions.resume([type]) : resumeEmpty;
  },
  _: () => resumeEmpty,
});

/**
 * Returns a list of direct user component types in the expression.
 * Used by the renderer to determine which subtrees to re-render.
 */
export const getDirectUserComponentDependencies =
  Expressions.everything<Array<Plugins.QualifiedElement>>(concat)(
    getUserComponentType
  );

const memoGetComponentMapView = weakMemo((components: Component.ComponentMap) =>
  weakMemo((componentType: Qualified<Component.ComponentName>) =>
    getComponentMapViewRecursive(
      components,
      componentType,
      QualifiedMap.empty()
    )
  )
);

/**
 * Allows you to filter the component map by a given component. For example, sometimes you need to work
 * with a page and any of the components that page may use. Operating on the entire component map can
 * be expensive for customers that have many components and this allows you to do less work by first
 * narrowing the component map only to components that may be relevant.
 *
 * For example, suppose we have a page like this:
 *
 * - Page
 *   - Component A
 *   - Block
 *
 * And we have a component map like this:
 *
 * {
 *   Page,
 *   Component A,
 *   Component B,
 *   Component C
 * }
 *
 * If we don't know which component to look inside of (the preferred approach!) then we can first narrow the
 * component map to only the components that are relevant to the page.
 *
 * Now we don't have to waste any time looking into Component B or Component C.
 *
 *
 * We run the dependency graph computation during render to optimize re-renders so this cache is generally
 * warmed out of the gate.
 *
 * @param components ComponentMap to filter, usually this is the ComponentMap in DesignerStore.
 * @param componentType Which component would you like to get the view of.
 * @returns  A filtered view of the component map with only the targeted componentType and its dependencies present.
 */
export function getComponentMapView(
  components: Component.ComponentMap,
  componentType: Qualified<Component.ComponentName>
): Component.ComponentMap {
  return memoGetComponentMapView(components)(componentType);
}

function getComponentMapViewRecursive(
  components: Component.ComponentMap,
  componentType: Qualified<Component.ComponentName>,
  componentMapView: Component.ComponentMap
): Component.ComponentMap {
  if (componentMapView.has(componentType)) return componentMapView;
  const component = components.get(componentType);
  if (!component) return componentMapView;

  const render = Component.getRender(component);
  const dependencies = getDirectUserComponentDependencies(render);

  for (const dependency of dependencies) {
    componentMapView = getComponentMapViewRecursive(
      components,
      dependency,
      componentMapView
    );
  }
  return componentMapView.set(componentType, component);
}

/**
 * Recursively searches through a component tree for an element matching the predicate starting from the `expression`.
 * Traverses all elements including those within nested user components.
 */
export const expressionContainsSpecificElement = weakMemo(
  (predicate: (arg1: ExpressionElement) => boolean) =>
    weakMemo((expression: Expression) =>
      weakMemo((components: Component.ComponentMap): boolean => {
        return Expressions.some(
          matchExpression({
            Element: (element) => {
              if (predicate(element)) {
                return true;
              }

              if (isUserComponentInst(components, element)) {
                const component = components.get(element.type);
                const componentRoot = component
                  ? Component.getRender(component)
                  : null;

                return componentRoot
                  ? expressionContainsSpecificElement(predicate)(componentRoot)(
                      components
                    )
                  : false;
              }

              return false;
            },
            _: constantFalse,
          })
        )(expression);
      })
    )
);

function completeElement(
  el: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement {
  const dataType = getDataTypeFromElement(plugins)(componentMap)(el);

  const currentData = Expressions.getElementData(el);
  const completedData = getCurrentOrDefaultExpression(plugins)(
    dataType,
    currentData
  );

  return Expressions.setElementData(el, completedData);
}
/**
 * Ensures that every element in the given expression has complete element data.
 * Uses default values based on element datatype to fill in missing data.
 * Does not touch element data of component instances.
 * @param rootExpr - The root expression to complete
 * @param componentMap - The component map
 * @param plugins - The plugins to use which are used to get the default values for the element data.
 * @returns The completed expression.
 */
export function completeTree(
  rootExpr: Expression,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Expression {
  return Expressions.everywhere((expr) => {
    const el = Expressions.getElement(expr);
    if (!el || isComponentInst(componentMap)(el)) {
      return expr;
    }

    return EElement(completeElement(el, componentMap, plugins));
  })(rootExpr);
}

const createGuidGenerator = ({
  namespace,
  overrideId,
}: {
  namespace: string;
  overrideId: string;
}) => {
  const seedUuid = uuidv5(overrideId, namespace);
  return new GuidGenerator(seedUuid);
};

/**
 * Re-mints every element id in `expression` the way an override write does, and
 * reports the old-to-new mapping next to the rewritten expression.
 *
 * The id sequence is a pure function of the seed and the traversal order, so a
 * caller that needs the ids before the write reaches the reducer can call this
 * to predict them. Keep this as the only place the seeding and the walk live,
 * otherwise a predicted id map can silently drift from the written one.
 * Callers can skip ids that are already owned outside the expression being
 * replaced without changing the sequence when no collision exists.
 *
 * `namespace` feeds `uuidv5` and has to be a uuid.
 */
export const reassignElementIdsForOverride = ({
  namespace,
  overrideId,
  expression,
  isElementIdTaken,
}: {
  namespace: string;
  overrideId: string;
  expression: Expression;
  isElementIdTaken?: (elementId: string) => boolean;
}): {expression: Expression; idMap: Record<string, string>} => {
  const idMap: Record<string, string> = {};
  const reassignElementId = matchExpression({
    Element: (element: ExpressionElement) => (guidGenerator: GuidGenerator) => {
      let nextId = guidGenerator.getNext();
      while (isElementIdTaken?.(nextId)) {
        nextId = guidGenerator.getNext();
      }
      idMap[element.id] = nextId;
      return pair(guidGenerator, EElement({...element, id: nextId}));
    },
    _: (value: Expression) => (guidGenerator: GuidGenerator) =>
      pair(guidGenerator, value),
  });

  return {
    expression: Expressions.everywhereWithContext(reassignElementId)(
      createGuidGenerator({namespace, overrideId}),
      expression
    ),
    idMap,
  };
};

type ElementDataUpdate = {
  key: ExpressionPath;
  newExpression: Expression;
};

/**
 * Bases an edit on the value the settings UI shows instead of on the raw
 * stored overrides.
 *
 * A prop that has never been overridden carries no expression in the instance
 * data, and `sparseSetIn` cannot build a `Distinct` (e.g. a Code prop) from
 * nothing: it throws, so the caller's reducer logs the error, keeps its state,
 * and the edit is lost. Seeding the prop with the default the editor rendered
 * makes those updates applicable. Callers that expand the prop defaults before
 * dispatching never hit this.
 */
const seedUnstoredFieldDefault = ({
  plugins,
  componentMap,
  el,
  currentHoleyExpression,
  dataType,
  key,
}: {
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  el: ExpressionElement;
  currentHoleyExpression: Expression;
  dataType: Type;
  key: ExpressionPath;
}): Expression => {
  const [firstPart, secondPart] = key;
  // Only seed descents into a `Distinct`. Other descents stay sparse so
  // untouched defaults are not persisted as overrides.
  if (firstPart?.in !== 'Record' || secondPart?.in !== 'Distinct') {
    return currentHoleyExpression;
  }

  const fieldId = firstPart.at;
  const fieldType = Types.getRecordFields(dataType)?.[fieldId];
  if (!fieldType) return currentHoleyExpression;

  const fieldPath: ExpressionPath = [{in: 'Record', at: fieldId}];
  const existing = Expressions.getIn(currentHoleyExpression, fieldPath);
  if (existing != null && !Expressions.isHole(existing)) {
    return currentHoleyExpression;
  }

  const component = componentMap.get(el.type);
  const componentDefault =
    component && isUserComponentInst(componentMap, el)
      ? getFieldDefaultExpression(component, fieldId, fieldType, plugins)
      : null;
  // A component default can be a binding (e.g. a localized default). The
  // `Distinct` edit would silently no-op on it and persist the binding as an
  // override, so fall back to the type default.
  const defaultValue =
    componentDefault && Expressions.getIn(componentDefault, [secondPart])
      ? componentDefault
      : getCurrentOrDefaultExpression(plugins)(fieldType);

  return Expressions.sparseSetIn(
    currentHoleyExpression,
    fieldPath,
    defaultValue
  );
};

/**
 * Applies one path update onto a holey expression and returns the next holey
 * expression (holes may still remain).
 */
const applyUpdateToHoleyExpression = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  el: ExpressionElement,
  currentHoleyExpression: Expression,
  key: ExpressionPath,
  newExpression: Expression
): Expression => {
  const dataType = getDataTypeFromElement(plugins)(componentMap)(el);

  currentHoleyExpression = seedUnstoredFieldDefault({
    plugins,
    componentMap,
    el,
    currentHoleyExpression,
    dataType,
    key,
  });

  // Merge missing expressions into the incoming data if we are updating the root level.
  let nextExpression = newExpression;
  if (key.length === 0) {
    nextExpression = addBackHiddenProperties(plugins)(nextExpression)(dataType)(
      currentHoleyExpression
    );
  }

  const fieldDataType = Types.__DEPRECATED__getTypeAtExpressionPath(
    dataType,
    key
  );

  if (
    isUserComponent(el.type) &&
    fieldDataType &&
    Types.isListOfElements(fieldDataType)
  ) {
    const existingValue = Expressions.getIn(currentHoleyExpression, key);

    const existingListOfElements = existingValue
      ? Expressions.getList(existingValue) || emptyArray
      : emptyArray;

    const lastKeyPathIndex = key.length - 1;
    const overrideId =
      key[lastKeyPathIndex]?.in === 'Record' ? key[lastKeyPathIndex].at : null;

    if (
      existingListOfElements.length === 0 &&
      overrideId &&
      typeof overrideId === 'string'
    ) {
      // When there is no override, some of the element IDs in nextExpression
      // can come from the default value. To avoid duplicate IDs in override
      // trees, we re-assign all the element IDs in the nextExpression to avoid
      // persisting default value IDs. We use GUID generator with a seed to
      // generate deterministic element IDs so that primary and secondary locales
      // get the same element IDs in overrides.
      nextExpression = reassignElementIdsForOverride({
        namespace: el.id,
        overrideId,
        expression: nextExpression,
      }).expression;
    }
  }

  return Expressions.sparseSetIn(currentHoleyExpression, key, nextExpression);
};

/**
 * Applies multiple element data updates against a single holey base, then
 * converts once. Avoids re-basing each change on a stale holey expression.
 *
 * @param plugins - WFDL plugins
 * @param componentMap - Current component map
 * @param currentElementHoleyExpression - Holey expression for the element (optional or null)
 * @param updates - Path/expression pairs to apply
 * @returns Function that takes the element to update and returns the updated element
 */
export const applyElementDataUpdates =
  (
    plugins: Plugins.PluginsType,
    componentMap: Component.ComponentMap,
    currentElementHoleyExpression: Expression | undefined | null,
    updates: Array<ElementDataUpdate>
  ): ((el: ExpressionElement) => ExpressionElement) =>
  (el: ExpressionElement): ExpressionElement => {
    const currentElementData = Expressions.getElementData(el);
    let currentHoleyExpression = currentElementHoleyExpression
      ? currentElementHoleyExpression
      : Expressions.toHoleyExpression(currentElementData);

    for (const {key, newExpression} of updates) {
      currentHoleyExpression = applyUpdateToHoleyExpression(
        plugins,
        componentMap,
        el,
        currentHoleyExpression,
        key,
        newExpression
      );
    }

    const newDataExpr = Expressions.fromHoleyExpression(currentHoleyExpression);
    if (!newDataExpr) {
      return el;
    }

    return Expressions.setElementData(el, newDataExpr);
  };

/**
 * Applies an element data update at the given path.
 * Handles component instances with typed data and regular elements.
 * Supports GUID reassignment for list of elements overrides.
 *
 * @param plugins - WFDL plugins
 * @param componentMap - Current component map
 * @param currentElementHoleyExpression - Holey expression for the element (optional or null)
 * @param key - Path to the field being updated
 * @param newExpression - New expression value
 * @returns Function that takes the element to update and returns the updated element
 */
export const applyElementDataUpdate = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  currentElementHoleyExpression: Expression | undefined | null,
  key: ExpressionPath,
  newExpression: Expression
): ((el: ExpressionElement) => ExpressionElement) =>
  applyElementDataUpdates(
    plugins,
    componentMap,
    currentElementHoleyExpression,
    [{key, newExpression}]
  );
